import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import axios from 'axios';
import config from '../../config';
import { normalizeTimezone } from '../utils/Date';

// Constants
const STORAGE_KEYS = {
  PARENT_AUTH_TOKEN: 'parentAuthToken',
  PARENT_USER_INFO: 'parentUserInfo',
  FORM_RETURN_URL: 'form_return_url',
};

const MESSAGE_TYPES = {
  GET_AUTH_CREDENTIALS: 'GET_AUTH_CREDENTIALS',
  AUTH_CREDENTIALS_RESPONSE: 'AUTH_CREDENTIALS_RESPONSE',
  INITIATE_LOGIN: 'INITIATE_LOGIN',
};

const AUTH_STATES = {
  SIGNED_IN: 'signedIn',
  SIGN_IN: 'signIn',
};

const TIMEOUTS = {
  PARENT_AUTH_CHECK: 1000,
};

const DEFAULT_TIMEZONE = 'UTC';

/**
 * Manages authentication for forms with iframe integration support
 */
class AuthenticationManager {
  // Domain utilities
  static extractBaseDomain(domain) {
    const parts = domain.split('.');
    return parts.length >= 2 ? parts.slice(-2).join('.') : domain;
  }

  static isInIframe() {
    return window.self !== window.top;
  }

  static isSameDomain() {
    if (!this.isInIframe()) return false;

    const currentDomain = window.location.hostname;
    const parentDomain = new URL(config.ADMIN_DASHBOARD_URL).hostname;

    return (
      this.extractBaseDomain(currentDomain) ===
      this.extractBaseDomain(parentDomain)
    );
  }

  // Storage utilities
  static getParentAuthToken() {
    return sessionStorage.getItem(STORAGE_KEYS.PARENT_AUTH_TOKEN);
  }

  static storeParentCredentials(credentials) {
    if (credentials.idToken) {
      sessionStorage.setItem(
        STORAGE_KEYS.PARENT_AUTH_TOKEN,
        credentials.idToken
      );
    }
    if (credentials.userInfo) {
      sessionStorage.setItem(
        STORAGE_KEYS.PARENT_USER_INFO,
        JSON.stringify(credentials.userInfo)
      );
    }
  }

  static clearParentCredentials() {
    sessionStorage.removeItem(STORAGE_KEYS.PARENT_AUTH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.PARENT_USER_INFO);
  }

  static getStoredUserInfo() {
    try {
      const userInfo = sessionStorage.getItem(STORAGE_KEYS.PARENT_USER_INFO);
      return userInfo ? JSON.parse(userInfo) : null;
    } catch (error) {
      console.error('Error parsing parent user info:', error);
      return null;
    }
  }

  // Authentication methods
  static async processIdToken() {
    try {
      const parentToken = this.getParentAuthToken();
      if (parentToken) return parentToken;

      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString();
    } catch (error) {
      console.error('Error processing ID token:', error);
      return null;
    }
  }

  static async isAuthenticated() {
    try {
      if (this.getParentAuthToken()) return true;

      const session = await fetchAuthSession();
      return !!session.tokens?.idToken;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  }

  static async getCurrentUserInfo(
    setState,
    uniqueNamesGenerator,
    uniqueNamesGeneratorConfig
  ) {
    try {
      const user = await getCurrentUser();
      if (user) {
        setState({
          authState: AUTH_STATES.SIGNED_IN,
          username: user.username,
        });
      }
    } catch (error) {
      console.log('NOT AUTHENTICATED');
      setState({
        authState: AUTH_STATES.SIGN_IN,
        username: uniqueNamesGenerator(uniqueNamesGeneratorConfig),
      });
    }
  }

  // Cross-domain authentication
  static async checkAppWorkflowAuthentication() {
    return new Promise((resolve) => {
      if (!this.isInIframe() || this.isSameDomain()) {
        resolve(false);
        return;
      }

      const handleMessage = this.createMessageHandler(resolve);
      window.addEventListener('message', handleMessage);

      this.sendCredentialsRequest();
      this.setAuthTimeout(handleMessage, resolve);
    });
  }

  static createMessageHandler(resolve) {
    const handler = (event) => {
      if (event.origin !== config.ADMIN_DASHBOARD_URL) return;

      if (event.data.type === MESSAGE_TYPES.AUTH_CREDENTIALS_RESPONSE) {
        window.removeEventListener('message', handler);

        if (event.data.authenticated && event.data.credentials) {
          this.storeParentCredentials(event.data.credentials);
          resolve(true);
        } else {
          resolve(false);
        }
      }
    };
    return handler;
  }

  static sendCredentialsRequest() {
    window.parent.postMessage(
      {
        type: MESSAGE_TYPES.GET_AUTH_CREDENTIALS,
        source: 'form_iframe',
      },
      config.ADMIN_DASHBOARD_URL
    );
  }

  static setAuthTimeout(handler, resolve) {
    setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(false);
    }, TIMEOUTS.PARENT_AUTH_CHECK);
  }

  // Login flow
  static async initiateUnifiedLogin() {
    try {
      const currentUrl = window.location.href;
      localStorage.setItem(STORAGE_KEYS.FORM_RETURN_URL, currentUrl);

      if (this.isInIframe()) {
        this.sendLoginRequest(currentUrl);
      } else {
        this.redirectToLogin(currentUrl);
      }
    } catch (error) {
      console.error('Error initiating unified login:', error);
    }
  }

  static sendLoginRequest(returnUrl) {
    window.parent.postMessage(
      {
        type: MESSAGE_TYPES.INITIATE_LOGIN,
        source: 'form_iframe',
        returnUrl,
      },
      config.ADMIN_DASHBOARD_URL
    );
  }

  static redirectToLogin(returnUrl) {
    const loginUrl = `${config.ADMIN_DASHBOARD_URL}/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = loginUrl;
  }

  // Component security check
  static async checkCanExecute(formId, history, setState) {
    try {
      const isSecureResponse = await this.checkIfSecureComponent(formId);
      const isSecure = isSecureResponse.data.data;

      if (isSecure) {
        setState({ form_oauth2: 'oauth2' });
        return this.handleSecureComponent(formId, history, setState);
      } else {
        setState({ canInitData: true });
      }
    } catch (error) {
      console.error('Error checking component execution permissions:', error);
    }
  }

  static checkIfSecureComponent(formId) {
    return axios(`${config.API_ADDRESS}/isSecureComponent?form_id=${formId}`, {
      method: 'get',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  static async handleSecureComponent(formId, history, setState) {
    const isAuthenticated = await this.isAuthenticated();
    if (!isAuthenticated) return;

    const idToken = await this.processIdToken();
    if (!idToken) return;

    try {
      const response = await this.checkComponentPermissions(formId, idToken);
      const canExecute = response.data;

      if (!canExecute) {
        history.push('/form_error');
      } else {
        setState({ canInitData: true });
      }
    } catch (error) {
      console.error('Error checking component permissions:', error);
    }
  }

  static checkComponentPermissions(formId, idToken) {
    return axios(
      `${config.PROJECT_SERVICE_URL}/canExecuteComponent/${formId}`,
      {
        method: 'get',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: idToken,
        },
      }
    );
  }

  // Auth Hub setup
  static setupAuthHub(processIdToken, initData, setState) {
    Hub.listen('auth', (data) => {
      const { event } = data.payload;

      switch (event) {
        case 'signedIn':
          this.handleSignedIn(processIdToken, initData, setState, data);
          break;
        case 'signInWithRedirect_failure':
        case 'signIn_failure':
          this.handleSignInFailure(setState, data);
          break;
        default:
          break;
      }
    });
  }

  static handleSignedIn(processIdToken, initData, setState, data) {
    processIdToken().then(initData);
    setState({
      authState: AUTH_STATES.SIGNED_IN,
      authData: data.payload.data,
    });
  }

  static handleSignInFailure(setState, data) {
    setState({
      authState: AUTH_STATES.SIGN_IN,
      authData: null,
      authError: data.payload.data,
    });
  }

  // User info and timezone
  static async setTimezone(setState) {
    const idToken = await this.processIdToken();
    if (!idToken) return;

    try {
      const response = await this.fetchUserInfo(idToken);
      this.updateUserTimezone(setState, response.data);
    } catch (error) {
      this.setDefaultTimezone(setState);
    }
  }

  static async fetchUserInfo(idToken) {
    try {
      const user = await getCurrentUser();
      return axios.get(`${config.USER_ADDRESS}/user-info`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: idToken,
          'user-id': user.username,
        },
      });
    } catch (error) {
      console.error('Error getting current user for user-info request:', error);
      throw error;
    }
  }

  static updateUserTimezone(setState, userData) {
    setState({
      timezone: normalizeTimezone(userData.timezone),
      userName: userData.userName || '',
      displayName: userData.displayName || '',
    });
  }

  static setDefaultTimezone(setState) {
    setState({ timezone: DEFAULT_TIMEZONE });
  }
}

// Development debugging support
if (process.env.NODE_ENV === 'development') {
  window.AuthenticationManager = AuthenticationManager;
}

export default AuthenticationManager;
